import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { uploadFile } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUp, Pencil, Plus, Trash2 } from "lucide-react";

const EMPTY = { post_type: "blog", title: "", content: "", image: "" };
const TYPES = [
  { value: "blog", label: "Blog (clinic article)" },
  { value: "forum", label: "Forum discussion" },
  { value: "testimonial", label: "Testimonial" },
];

/** Admin → Community: write, correct and remove anything on the community/blog page. */
export const CommunityTab = () => {
  const [posts, setPosts] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [editing, setEditing] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => setPosts((await api.get("/posts")).data);
  useEffect(() => { load(); }, []);

  const startEdit = (p) => {
    setDraft({ post_type: p.post_type, title: p.title, content: p.content, image: p.image || "" });
    setEditing(p.id);
    setOpen(true);
  };

  const save = async () => {
    if (!draft.title.trim() || !draft.content.trim()) return toast.error("A title and some text are needed");
    setBusy(true);
    try {
      if (editing) await api.put(`/posts/${editing}`, draft);
      else await api.post("/posts", draft);
      toast.success(editing ? "Post updated" : "Published");
      setDraft(EMPTY);
      setEditing("");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/posts/${id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { url } = await uploadFile(file, "post", { maxWidth: 1600 });
      setDraft((d) => ({ ...d, image: url }));
      toast.success("Image attached");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="community-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Blog articles, forum threads and testimonials — all editable here.</p>
        <Button
          onClick={() => { setOpen((v) => !v); setEditing(""); setDraft(EMPTY); }}
          className="btn-pill bg-primary text-primary-foreground h-10"
          data-testid="post-add-toggle"
        >
          <Plus size={14} className="mr-1.5" /> New Post
        </Button>
      </div>

      {open && (
        <Card className="border-border">
          <CardContent className="p-6 space-y-4">
            <h3 className="font-display text-2xl">{editing ? "Edit post" : "New post"}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="overline">Type</Label>
                <Select value={draft.post_type} onValueChange={(v) => setDraft((d) => ({ ...d, post_type: v }))}>
                  <SelectTrigger className="mt-2" data-testid="post-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Title</Label>
                <Input value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} className="mt-2 bg-transparent" data-testid="post-title" />
              </div>
            </div>
            <div>
              <Label className="overline">Content</Label>
              <Textarea rows={6} value={draft.content} onChange={(e) => setDraft((d) => ({ ...d, content: e.target.value }))} className="mt-2 bg-transparent" data-testid="post-content" />
            </div>
            <div className="flex flex-wrap items-center gap-4">
              {draft.image && <img src={mediaUrl(draft.image)} alt="" className="w-24 h-16 object-cover rounded-lg border border-border" />}
              <label className="btn-pill border border-border px-4 py-2 text-xs cursor-pointer hover:border-primary/40 inline-flex items-center gap-1.5">
                <ImageUp size={13} /> Attach image
                <input type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="post-image" />
              </label>
              <Button onClick={save} disabled={busy} className="btn-pill bg-primary text-primary-foreground h-10 ml-auto" data-testid="post-save">
                {busy ? "Saving…" : editing ? "Save changes" : "Publish"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {posts.length === 0 && <p className="text-sm text-muted-foreground" data-testid="posts-empty">Nothing published yet.</p>}
        {posts.map((p) => (
          <Card key={p.id} className="border-border" data-testid={`post-row-${p.id}`}>
            <CardContent className="p-5 flex items-start gap-4">
              {p.image && <img src={mediaUrl(p.image)} alt="" className="w-16 h-16 rounded-lg object-cover border border-border shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-display text-xl">{p.title}</span>
                  <Badge variant="outline" className="text-[10px] capitalize">{p.post_type}</Badge>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{p.content}</p>
                <div className="text-xs text-muted-foreground mt-1">
                  {p.author_name} · {new Date(p.created_at).toLocaleDateString()} · {p.comments?.length || 0} comment(s)
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => startEdit(p)} className="text-muted-foreground hover:text-primary" data-testid={`post-edit-${p.id}`}>
                  <Pencil size={15} />
                </button>
                <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`post-delete-${p.id}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default CommunityTab;
